// Multi-número por sucursal (Victor 2026-05-23)
// ------------------------------------------------------------------
// Cuando una sucursal (o el super admin en su sucursal personal) tiene
// 2+ números de WhatsApp conectados, el panel del cliente muestra un
// toggle "Responder desde" y el chat se ve UNIFICADO con los mensajes de
// ambos números, cada uno con su color. Este helper centraliza:
//   1. qué números WhatsApp operativos tiene una sucursal, y
//   2. el color (acento) que le toca a cada número por su posición.
// Lo usan LeadCardPanel (toggle), MessagesView (merge) y Message (color).

const WA_CHANNELS = ['Channel::Api', 'Channel::Whatsapp'];

// Acentos por posición del número dentro de su sucursal. Solo los 2
// primeros números reciben color (azul / verde); de haber un 3ro, sin color.
export const NUMBER_ACCENTS = ['azul', 'verde'];

// Roles · 2026-05-30 (Victor · P2): cada inbox WhatsApp dentro de una sucursal
// multi-número tiene un rol identificable: Principal (dorado), Secundario
// (azul), Makeup (rosa). Se detecta por nombre cuando contiene "makeup"; el
// resto se ordena por id (1ro=Principal, 2do=Secundario). Reemplaza el sistema
// de azul/verde por posición que no distinguía bien cuando los números
// terminaban en los mismos 4 dígitos (ej. PERSP 8495280461 vs 8495050461).
const ROLE_DEFINITIONS = {
  principal: { label: 'Principal', color: '#c9a35a', accentClass: 'gold' },
  secundario: { label: 'Secundario', color: '#3b82f6', accentClass: 'blue' },
  makeup: { label: 'Makeup', color: '#ec4899', accentClass: 'pink' },
};

export function roleForInbox(store, inboxId) {
  const inbox = store.getters['inboxes/getInbox'](inboxId);
  if (!inbox) return null;
  const list = branchWaInboxes(store, inbox.branch_id);
  if (list.length < 2) return null;

  // 1. Detección directa por nombre: contiene "makeup" → Makeup
  if (/makeup/i.test(inbox.name || '')) return { key: 'makeup', ...ROLE_DEFINITIONS.makeup };

  // 2. Resto: por posición entre los NO-makeup (primero=Principal, segundo=Secundario)
  const nonMakeup = list.filter(i => !/makeup/i.test(i.name || ''));
  const idx = nonMakeup.findIndex(i => i.id === inboxId);
  if (idx === 0) return { key: 'principal', ...ROLE_DEFINITIONS.principal };
  if (idx === 1) return { key: 'secundario', ...ROLE_DEFINITIONS.secundario };
  return null;
}

// Los inbox WAHA/Bridge son Channel::Api y NO tienen campo phone: el número
// vive dentro del nombre ("SDE WhatsApp +18494600461"). Tomamos la corrida
// de dígitos más larga; <8 dígitos = "sin número".
export function inboxPhoneDigits(inbox) {
  const raw = inbox.phone_number
    ? String(inbox.phone_number).replace(/\D/g, '')
    : '';
  if (raw.length >= 8) return raw;
  const runs = String(inbox.name || '').match(/[\d][\d\s().+-]{6,}[\d]/g) || [];
  let best = '';
  runs.forEach(r => {
    const d = r.replace(/\D/g, '');
    if (d.length > best.length) best = d;
  });
  return best.length >= 8 ? best : null;
}

// ¿Está operativo? Mismo criterio que el picker "Escribir por…": WAHA reporta
// conn_status confiable; el bridge Baileys casi nunca lo setea, así que se
// considera conectado salvo que esté EXPLÍCITAMENTE 'disconnected'.
function isInboxOperational(inbox) {
  const wh = String(inbox.webhook_url || '').toLowerCase();
  const isWaha = wh.includes('waha');
  const connected =
    inbox.channel_type !== 'Channel::Api' ||
    inbox.additional_attributes?.conn_status === 'connected' ||
    (!isWaha && inbox.additional_attributes?.conn_status !== 'disconnected');
  const isDup = /no\s*usar|duplicad/i.test(inbox.name || '');
  return connected && !isDup && !!inboxPhoneDigits(inbox);
}

// Números WhatsApp operativos de una sucursal, ordenados de forma estable por
// id para que el color de cada número no cambie entre sesiones.
export function branchWaInboxes(store, branchId) {
  if (branchId == null) return [];
  return (store.getters['inboxes/getInboxes'] || [])
    .filter(
      i =>
        WA_CHANNELS.includes(i.channel_type) &&
        i.branch_id === branchId &&
        isInboxOperational(i)
    )
    .sort((a, b) => a.id - b.id);
}

// ¿Esta conversación vive en una sucursal con 2+ números? (gate del feature)
export function isMultiNumberInbox(store, inboxId) {
  const inbox = store.getters['inboxes/getInbox'](inboxId);
  if (!inbox) return false;
  return branchWaInboxes(store, inbox.branch_id).length >= 2;
}

// Color que le toca a un número por su posición en la sucursal. Devuelve null
// si la sucursal tiene <2 números o si el número es el 3ro+.
export function accentForInbox(store, inboxId) {
  const inbox = store.getters['inboxes/getInbox'](inboxId);
  if (!inbox) return null;
  const list = branchWaInboxes(store, inbox.branch_id);
  if (list.length < 2) return null;
  const idx = list.findIndex(i => i.id === inboxId);
  return idx >= 0 ? NUMBER_ACCENTS[idx] || null : null;
}
